import { useId, useMemo, useState } from 'react'
import FloatingMenuPopup from '@/components/FloatingMenuPopup'
import { ChevronDown20Regular } from '@/components/Icon'
import { ButtonBase, Typography } from '@mui/material'
import styles from './homeFloatingSelect.module.scss'

export default function HomeFloatingSelect({
  'aria-label': ariaLabel,
  placeholder,
  value,
  options,
  onChange,
  menuTitle,
  menuAnchorOrigin = { vertical: 'bottom', horizontal: 'left' },
  menuTransformOrigin = { vertical: 'top', horizontal: 'left' },
  variant = 'default',
}) {
  const listId = useId()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)
  const hasSelection = Boolean(value)
  const selectedOption = useMemo(() => options.find((o) => o.value === value), [options, value])
  const SelectedIcon = selectedOption?.Icon
  const triggerLabel = selectedOption?.label ?? placeholder
  const isPlaceholder = !hasSelection
  const reserveIconSpace = useMemo(() => options.some((o) => o.Icon), [options])
  const menuItems = useMemo(
    () =>
      options.map((o) => ({
        id: o.value,
        label: o.label,
        Icon: o.Icon,
        selected: o.value === value,
      })),
    [options, value],
  )

  return (
    <>
      <ButtonBase
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={ariaLabel}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className={`${styles.trigger}${variant === 'ghost' ? ` ${styles.triggerGhost}` : ''}${
          variant === 'landing' ? ` ${styles.triggerLanding}` : ''
        }`}
      >
        {SelectedIcon && variant !== 'landing' ? (
          <span className={styles.triggerIcon} aria-hidden>
            <SelectedIcon className={styles.triggerIconSvg} />
          </span>
        ) : null}
        <Typography
          component="span"
          variant="body2"
          color={isPlaceholder ? 'var(--app-text-placeholder)' : 'text.primary'}
          className={`${styles.label}${isPlaceholder ? ` ${styles.labelPlaceholder}` : ''}`}
        >
          {triggerLabel}
        </Typography>
        <span className={`${styles.chevron}${open ? ` ${styles.chevronOpen}` : ''}`}>
          <ChevronDown20Regular aria-hidden />
        </span>
      </ButtonBase>
      <FloatingMenuPopup
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        items={menuItems}
        hideBackdrop
        reserveIconSpace={reserveIconSpace}
        onItemSelect={(id) => {
          onChange(id)
          setAnchorEl(null)
        }}
        anchorOrigin={menuAnchorOrigin}
        transformOrigin={menuTransformOrigin}
        menuListId={listId}
        menuListAriaLabel={ariaLabel}
        paperClassName={styles.menuPaper}
        header={
          menuTitle ? (
            <Typography component="span" variant="body2" className={styles.menuTitle}>
              {menuTitle}
            </Typography>
          ) : undefined
        }
        paperSx={
          menuTitle
            ? {
                width: 'fit-content',
                minWidth: 'var(--app-popup-min-width)',
                maxWidth: 'none',
              }
            : undefined
        }
      />
    </>
  )
}
